import { useState } from 'react';
import { BORNES_BULLE } from '@nextilt/commun';
import type { BulleDeFeuille } from '../partage/api/types';
import { refusDeCreation } from '../partage/creation';
import { useCreer, useFeuille } from '../partage/useArbre';
import { PastilleEtat } from '../apparence';
import {
  AJOUTER,
  AJOUTER_UNE_ETAPE_DEDANS,
  CREUSER,
  ouvrirLaFicheDe,
  PAS_D_ETAPE_DEDANS,
  SES_ETAPES,
} from '../partage/vocabulaire';
import './EtapesDeLaFiche.css';

/**
 * « Ses étapes », dans la fiche au téléphone (ticket 08 du chantier telephone,
 * `design/telephone.html`, « La fiche ») : ce que la bulle contient, lu sur sa propre
 * feuille (la même clé que partout, souvent déjà en cache), dans l'ordre de la Hiérarchie (ton
 * rang, puis l'arrivée). Chaque étape ouvre sa fiche d'un toucher. En bas, un champ crée une
 * étape dedans, et la liste la montre dès que le serveur l'a rangée (`useCreer` relit la
 * feuille : pas de place devinée, SPEC §5.4).
 *
 * Une étape montre son état (la pastille de toujours) et son titre, rien d'autre : la fiche
 * est déjà longue, ses points se lisent en l'ouvrant.
 */
export function EtapesDeLaFiche({
  bulleId,
  onEtape,
  onCreuser,
}: {
  bulleId: string;
  onEtape: (id: string) => void;
  onCreuser: () => void;
}) {
  const feuille = useFeuille(bulleId);
  const creer = useCreer(bulleId);
  const [titre, setTitre] = useState('');
  const refus = refusDeCreation(creer.error);

  const parId = new Map((feuille.data?.enfants ?? []).map((e) => [e.id, e]));
  const etapes = (feuille.data?.ordreHierarchique ?? [])
    .map((id) => parId.get(id))
    .filter((e): e is BulleDeFeuille => !!e);

  function ajouter() {
    const propre = titre.trim();
    if (!propre || creer.isPending) return;
    // Derrière la fiche, la feuille de la mère compte les étapes (« 3 étapes ») : `useCreer` relit
    // toutes les feuilles.
    creer.mutate(propre, { onSuccess: () => setTitre('') });
  }

  return (
    <section className="champs-bloc etapes">
      <div className="champs-etiquette">
        <h3>{SES_ETAPES}</h3>
      </div>

      {feuille.isPending ? null : etapes.length ? (
        <ul className="etapes-liste">
          {etapes.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                className="etapes-etape"
                aria-label={ouvrirLaFicheDe(e.titre)}
                onClick={() => onEtape(e.id)}
              >
                <span className="etapes-pastille" aria-hidden="true">
                  <PastilleEtat etat={e.etat} taille={16} />
                </span>
                <span className="etapes-titre">{e.titre}</span>
                <span className="etapes-chevron" aria-hidden="true">
                  ›
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="etapes-vide">{PAS_D_ETAPE_DEDANS}</p>
      )}

      <form
        className="etapes-ajout"
        onSubmit={(e) => {
          e.preventDefault();
          ajouter();
        }}
      >
        <input
          className="etapes-champ"
          value={titre}
          maxLength={BORNES_BULLE.titreMax}
          placeholder={AJOUTER_UNE_ETAPE_DEDANS}
          aria-label={AJOUTER_UNE_ETAPE_DEDANS}
          onChange={(e) => setTitre(e.target.value)}
        />
        <button
          type="submit"
          className="etapes-plus"
          aria-label={AJOUTER}
          disabled={!titre.trim() || creer.isPending}
        >
          +
        </button>
      </form>

      {/* Sans réseau, l'étape ne part pas (spec `telephone`, « Sans réseau ») : le titre reste dans le champ. */}
      {refus && (
        <p className="refus refus-sous-le-champ" role="status">
          {refus}
        </p>
      )}

      {/* « Creuser » quitte la fiche pour entrer dans la bulle : il vit là où l'on parle de ce qu'elle contient. */}
      <button type="button" className="bouton bouton-discret etapes-creuser" onClick={onCreuser}>
        {CREUSER}
      </button>
    </section>
  );
}
